<template>
  <div class="modal-backdrop">
    <div class="modal-content" role="dialog" aria-modal="true" aria-labelledby="layer-selector-title">
      <header class="modal-header">
        <div class="modal-title-group">
          <h3 id="layer-selector-title">Seleccionar capas</h3>
          <p>Elige las capas vulnerables que se incluirán en el análisis.</p>
        </div>
        <button class="modal-close" type="button" aria-label="Cerrar selector de capas" @click="$emit('close')">
          &times;
        </button>
      </header>

      <div class="modal-body">
        <div v-if="isLoading" class="layers-status">
          <span class="status-spinner" aria-hidden="true"></span>
          Cargando capas vulnerables…
        </div>
        <div v-else-if="categories.length === 0" class="layers-status">
          No hay capas habilitadas como vulnerables.
        </div>
        <div v-else class="layers-list">
          <div class="select-all-header">
            <label class="category-header-toggle">
              <input
                type="checkbox"
                :checked="allCheckState === 'all'"
                v-indeterminate="allCheckState === 'some'"
                @change="toggleAll"
                aria-label="Seleccionar todas las capas"
              >
              <span class="select-all-copy">
                <strong>Seleccionar todo</strong>
                <small>{{ totalLayerCount }} capas disponibles</small>
              </span>
            </label>
            <span class="selection-badge">{{ selectedLayerCount }}/{{ totalLayerCount }}</span>
          </div>

          <div class="categories-list">
            <section v-for="(category, index) in categories" :key="category.name || index" class="category-item">
              <!-- Categoría: checkbox activa/desactiva todas sus capas de una vez
                   (mismo patrón que Listado de Capas > Activar Categoría) -->
              <div class="category-header">
                <label class="category-header-toggle">
                  <input
                    type="checkbox"
                    :checked="categoryCheckState(category) === 'all'"
                    v-indeterminate="categoryCheckState(category) === 'some'"
                    @change="toggleCategory(category)"
                    :aria-label="`Activar todas las capas de ${category.name}`"
                  >
                  <strong>{{ category.name }}</strong>
                </label>
                <div class="category-actions">
                  <span class="category-count">
                    {{ categorySelectedCount(category) }}/{{ category.layers.length }}
                  </span>
                  <button
                    type="button"
                    class="category-toggle"
                    :aria-expanded="isCategoryExpanded(category)"
                    :aria-label="`${isCategoryExpanded(category) ? 'Cerrar' : 'Abrir'} categoría ${category.name}`"
                    @click="toggleCategoryExpanded(category)"
                  >
                    <font-awesome-icon :icon="isCategoryExpanded(category) ? 'chevron-up' : 'chevron-down'" />
                  </button>
                </div>
              </div>

              <!-- Capas de la categoría -->
              <div v-show="isCategoryExpanded(category)" class="layer-items">
                <div
                  v-for="layer in category.layers"
                  :key="layer.id"
                  class="layer-item"
                  :class="{ 'is-selected': internalSelectedLayers.includes(layer.id) }"
                >
                  <label>
                    <input
                      type="checkbox"
                      :value="layer.id"
                      v-model="internalSelectedLayers"
                    >
                    <span>{{ layer.name }}</span>
                  </label>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>

      <footer class="modal-actions">
        <span class="selection-summary">
          <strong>{{ selectedLayerCount }}</strong> de {{ totalLayerCount }} capas seleccionadas
        </span>
        <div class="action-buttons">
          <button class="btn-cancel" type="button" @click="$emit('close')">Cancelar</button>
          <button class="btn-save" type="button" @click="saveSelection">Guardar selección</button>
        </div>
      </footer>
    </div>
  </div>
</template>

<script>
import { LayerService } from '@/services/layers/layerService';
import { groupVulnerableLayers } from '@/components/mapTools/geoprocessings/utils/vulnerableLayers';

export default {
  name: 'GeoprocesoSeleccionCapas',
  props: {
    selectedLayers: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      internalSelectedLayers: [],
      vulnerableLayers: [],
      isLoading: false,
      collapsedCategories: {},
    };
  },
  computed: {
    categories() {
      return groupVulnerableLayers(
        this.vulnerableLayers,
        this.$store.getters.getLayers || []
      );
    },
    totalLayerCount() {
      return this.categories.reduce((total, category) => total + category.layers.length, 0);
    },
    selectedLayerCount() {
      return this.categories.reduce(
        (total, category) => total + this.categorySelectedCount(category),
        0
      );
    },
    // 'all' si están todas las capas de todas las categorías seleccionadas,
    // 'none' si ninguna, 'some' en el medio (deja el checkbox en indeterminate).
    allCheckState() {
      const allLayers = this.categories.flatMap(category => category.layers);
      const total = allLayers.length;
      if (total === 0) return 'none';
      const selectedCount = allLayers.filter(
        layer => this.internalSelectedLayers.includes(layer.id)
      ).length;
      if (selectedCount === 0) return 'none';
      return selectedCount === total ? 'all' : 'some';
    },
  },
  async mounted() {
    // Inicializar con las capas ya seleccionadas
    this.internalSelectedLayers = [...this.selectedLayers];
    this.isLoading = true;
    try {
      this.vulnerableLayers = await LayerService.getVulnerableLayers(this.$route.params.idMap);
      const currentIds = new Set(this.vulnerableLayers.map(layer => String(layer.id)));
      this.internalSelectedLayers = this.internalSelectedLayers.filter(
        id => currentIds.has(String(id))
      );
    } finally {
      this.isLoading = false;
    }
  },
  directives: {
    // El indeterminate del checkbox es una propiedad del DOM, no un atributo:
    // no existe un binding declarativo para él, hay que fijarlo a mano.
    indeterminate: {
      update(el, binding) {
        el.indeterminate = !!binding.value;
      },
    },
  },
  methods: {
    saveSelection() {
      this.$emit('save', this.internalSelectedLayers);
    },
    // 'all' si están todas las capas de la categoría seleccionadas, 'none' si
    // ninguna, 'some' en el medio (deja el checkbox en indeterminate).
    categoryCheckState(category) {
      const total = category.layers.length;
      if (total === 0) return 'none';
      const selectedCount = this.categorySelectedCount(category);
      if (selectedCount === 0) return 'none';
      return selectedCount === total ? 'all' : 'some';
    },
    categorySelectedCount(category) {
      return category.layers.filter(
        layer => this.internalSelectedLayers.includes(layer.id)
      ).length;
    },
    isCategoryExpanded(category) {
      return !this.collapsedCategories[category.name];
    },
    toggleCategoryExpanded(category) {
      this.$set(
        this.collapsedCategories,
        category.name,
        !this.collapsedCategories[category.name]
      );
    },
    toggleAll() {
      const allLayerIds = this.categories.flatMap(category => category.layers.map(layer => layer.id));
      if (this.allCheckState === 'all') {
        this.internalSelectedLayers = [];
      } else {
        this.internalSelectedLayers = [...new Set([...this.internalSelectedLayers, ...allLayerIds])];
      }
    },
    toggleCategory(category) {
      const layerIds = category.layers.map(layer => layer.id);
      if (this.categoryCheckState(category) === 'all') {
        this.internalSelectedLayers = this.internalSelectedLayers.filter(
          id => !layerIds.includes(id)
        );
      } else {
        const toAdd = layerIds.filter(id => !this.internalSelectedLayers.includes(id));
        this.internalSelectedLayers = [...this.internalSelectedLayers, ...toAdd];
      }
    },
  },
};
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  inset: 0;
  padding: 24px;
  background: rgba(8, 28, 44, 0.62);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-content {
  padding: 0;
  background: var(--color-white, #fff);
  border: 1px solid rgba(59, 110, 148, 0.22);
  border-radius: 12px;
  width: min(100%, 560px);
  max-height: min(86vh, 760px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(8, 28, 44, 0.32);
}

.modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding: 18px 24px;
  border-bottom: 3px solid var(--color-orange, #e8730c);
  background: var(--color-navy, #0e2a40);
  color: var(--color-white, #fff);
}

.modal-title-group {
  min-width: 0;
}

h3 {
  margin: 0;
  color: inherit;
  font-size: 1.2rem;
  line-height: 1.3;
}

.modal-header p {
  margin: 4px 0 0;
  color: var(--color-fog, #dbe1e6);
  font-size: 0.78rem;
  font-weight: 400;
}

.modal-close {
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  color: var(--color-fog, #dbe1e6);
  font-size: 1.45rem;
  font-weight: 400;
  line-height: 1;
}

.modal-close:hover {
  border-color: rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.1);
  color: var(--color-white, #fff);
}

.modal-body {
  flex: 1;
  min-height: 0;
  display: flex;
  padding: 18px 20px 0;
  background: var(--color-white, #fff);
}

.layers-list {
  flex: 1;
  /* Sin esto un hijo flex no se encoge por debajo del alto de su contenido
     (min-height:auto por defecto): la lista nunca llegaba a activar su propio
     scroll y el modal se cortaba contra max-height sin barra visible. */
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--color-line, #dbe1e6);
  border-radius: 8px;
  background: var(--color-white, #fff);
}

.layers-status {
  flex: 1;
  min-height: 160px;
  padding: 36px 18px;
  border: 1px solid var(--color-line, #dbe1e6);
  border-radius: 8px;
  background: var(--color-white, #fff);
  color: var(--color-graphite, #2b3138);
  text-align: center;
}

.status-spinner {
  display: block;
  width: 24px;
  height: 24px;
  margin: 0 auto 12px;
  border: 3px solid var(--color-fog, #dbe1e6);
  border-top-color: var(--color-orange, #e8730c);
  border-radius: 50%;
  animation: layer-selector-spin 0.8s linear infinite;
}

@keyframes layer-selector-spin {
  to { transform: rotate(360deg); }
}

.select-all-header {
  flex: 0 0 auto;
  min-height: 62px;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border-bottom: 1px solid var(--color-line, #dbe1e6);
  background: var(--color-white, #fff);
  color: var(--color-navy, #0e2a40);
}

.category-header-toggle {
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
  cursor: pointer;
}

.category-header-toggle strong {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.select-all-copy {
  display: flex;
  flex-direction: column;
}

.select-all-copy small {
  margin-top: 1px;
  color: var(--color-steel, #3b6e94);
  font-size: 0.68rem;
  font-weight: 500;
}

.category-header-toggle input[type="checkbox"],
.layer-item input[type="checkbox"] {
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  margin: 0;
  accent-color: var(--color-orange, #e8730c);
  cursor: pointer;
}

.selection-badge,
.category-count {
  flex: 0 0 auto;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 700;
}

.selection-badge {
  padding: 4px 9px;
  border: 1px solid var(--color-fog, #dbe1e6);
  background: var(--color-mist, #f2f4f6);
  color: var(--color-steel, #3b6e94);
}

.categories-list {
  min-height: 0;
  padding: 12px;
  overflow-y: auto;
  scrollbar-color: var(--color-ghost, #8a929a) transparent;
  scrollbar-width: thin;
}

.category-item {
  overflow: hidden;
  margin-bottom: 10px;
  border: 1px solid var(--color-line, #dbe1e6);
  border-radius: 7px;
  background: var(--color-white, #fff);
}

.category-item:last-child {
  margin-bottom: 0;
}

.category-header {
  min-height: 42px;
  padding: 9px 11px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-left: 3px solid var(--color-steel, #3b6e94);
  background: var(--color-mist, #f2f4f6);
  color: var(--color-navy, #0e2a40);
  font-size: 0.8rem;
}

.category-count {
  padding: 2px 7px;
  border: 1px solid var(--color-fog, #dbe1e6);
  background: var(--color-white, #fff);
  color: var(--color-steel, #3b6e94);
}

.category-actions {
  display: flex;
  align-items: center;
  gap: 5px;
}

.category-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 28px;
  width: 28px;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: var(--color-steel, #3b6e94);
}

.category-toggle:hover {
  background: var(--color-white, #fff);
  color: var(--color-navy, #0e2a40);
}

.layer-items {
  padding: 4px 7px 6px;
}

.layer-item {
  border-radius: 5px;
  color: var(--color-graphite, #2b3138);
  transition: background-color 0.15s ease, color 0.15s ease;
}

.layer-item:hover {
  background: var(--color-mist, #f2f4f6);
  color: var(--color-navy, #0e2a40);
}

.layer-item.is-selected {
  background: rgba(232, 115, 12, 0.08);
}

.layer-item label {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 34px;
  padding: 6px 8px;
  cursor: pointer;
  font-size: 0.78rem;
}

.modal-actions {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  padding: 14px 20px 16px;
  border-top: 1px solid var(--color-line, #dbe1e6);
  background: var(--color-white, #fff);
}

.selection-summary {
  margin-right: auto;
  color: var(--color-ghost, #8a929a);
  font-size: 0.7rem;
}

.selection-summary strong {
  color: var(--color-navy, #0e2a40);
}

.action-buttons {
  display: flex;
  gap: 9px;
}

button {
  min-height: 36px;
  padding: 8px 15px;
  border: 1px solid transparent;
  border-radius: 5px;
  cursor: pointer;
  font: inherit;
  font-size: 0.75rem;
  font-weight: 700;
  transition: background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}

button:focus-visible,
input:focus-visible {
  outline: 3px solid rgba(232, 115, 12, 0.28);
  outline-offset: 2px;
}

.btn-cancel {
  border-color: var(--color-line, #dbe1e6);
  background: var(--color-white, #fff);
  color: var(--color-graphite, #2b3138);
}

.btn-cancel:hover {
  border-color: var(--color-steel, #3b6e94);
  background: var(--color-mist, #f2f4f6);
  color: var(--color-navy, #0e2a40);
}

.btn-save {
  background: var(--color-navy, #0e2a40);
  color: var(--color-white, #fff);
  box-shadow: 0 3px 8px rgba(14, 42, 64, 0.18);
}

.btn-save:hover {
  background: var(--color-steel, #3b6e94);
}

@media (max-width: 600px) {
  .modal-backdrop {
    padding: 12px;
  }

  .modal-content {
    max-height: 92vh;
  }

  .modal-header,
  .modal-body,
  .modal-actions {
    padding-left: 16px;
    padding-right: 16px;
  }

  .modal-actions {
    align-items: stretch;
    flex-direction: column;
    gap: 10px;
  }

  .selection-summary {
    margin-right: 0;
    text-align: center;
  }

  .action-buttons button {
    flex: 1;
  }
}
</style>
